/**
 * Bulk order table
 * ---------------------------------------------------------------------
 * Cart AJAX updates still go through the theme's own, proven
 * form.updateItemQuantity() method (assets/quick-order-list.js), so cart
 * drawer sync, loading states, and error/pubsub wiring are all reused
 * as-is. This file takes over three things the theme's own markup
 * doesn't handle reliably enough for a full variant table:
 *
 * 1. Qty +/- buttons: the theme's <quantity-input> element uses the
 *    native input.stepUp()/stepDown(), which only steps correctly when
 *    the current value already sits on a step boundary measured from 0.
 *    Whenever a variant's quantity_rule.min isn't itself a multiple of
 *    quantity_rule.increment, stepUp()/stepDown() "snaps" to the nearest
 *    0-based multiple first, producing an unexpected jump. We compute
 *    the correct next value ourselves (min, min+increment, min+2*increment...)
 *    instead of relying on the native method.
 *
 * 2. Typed quantity entry: the theme's own change validation checks
 *    `value % step !== 0` (relative to 0), which incorrectly rejects
 *    valid quantities whenever min isn't a multiple of increment. We
 *    validate relative to min instead, matching Shopify's real rule.
 *
 * 3. The remove ("x") link: relies on the is="quick-order-list-remove"
 *    customized built-in element, which Safari does not upgrade at all
 *    (it doesn't support the `is` attribute), and its href falls back to
 *    a blank/incorrect URL if JS doesn't intercept the click in time —
 *    causing a full page reload instead of removing the row. We handle
 *    the click ourselves and preventDefault unconditionally.
 *
 * All three are intercepted in the CAPTURE phase at the document level,
 * scoped to elements inside [data-bulk-order-table], so the theme's own
 * separate "Quick order list" section (if used elsewhere) is untouched.
 * ---------------------------------------------------------------------
 */
(function () {
  var TABLE_SELECTOR = '[data-bulk-order-table]';
  var DEBOUNCE_MS = 300;
  var pendingTimers = new Map(); // variantId -> timeout id
  var sortState = new Map(); // BulkOrderTable-{sectionId} -> { key, direction }

  function withinBulkTable(el) {
    return !!(el && el.closest(TABLE_SELECTOR));
  }

  function getStrings() {
    var strings = (window.FoxTheme && window.FoxTheme.quickOrderListStrings) || {};
    return {
      min: strings.minError || 'This item has a minimum of [min]',
      max: strings.maxError || 'This item has a maximum of [max]',
      step: strings.stepError || 'You can only add this item in increments of [step]',
    };
  }

  function getForm(el) {
    return el.closest('form[is="quick-order-list"]');
  }

  function getRules(input) {
    var maxAttr = input.getAttribute('max');
    return {
      min: parseInt(input.getAttribute('data-min'), 10) || 1,
      max: maxAttr !== null && maxAttr !== '' ? parseInt(maxAttr, 10) : null,
      increment: parseInt(input.getAttribute('step'), 10) || 1,
    };
  }

  function validateQuantity(value, rules) {
    if (value === 0) return { valid: true };
    if (value < rules.min) {
      return { valid: false, message: getStrings().min.replace('[min]', rules.min) };
    }
    if (rules.max !== null && value > rules.max) {
      return { valid: false, message: getStrings().max.replace('[max]', rules.max) };
    }
    if ((value - rules.min) % rules.increment !== 0) {
      return { valid: false, message: getStrings().step.replace('[step]', rules.increment) };
    }
    return { valid: true };
  }

  function refreshButtonStates(input) {
    var wrapper = input.closest('quantity-input');
    if (wrapper && typeof wrapper.validateQtyRules === 'function') {
      wrapper.validateQtyRules();
    }
  }

  function commitQuantity(input, value) {
    var form = getForm(input);
    if (!form || typeof form.updateItemQuantity !== 'function') return;

    input.value = value;
    input.setAttribute('data-cart-quantity', value);
    refreshButtonStates(input);

    var variantId = input.getAttribute('data-index');
    var variantKey = parseInt(variantId, 10);

    if (pendingTimers.has(variantKey)) {
      clearTimeout(pendingTimers.get(variantKey));
    }
    pendingTimers.set(
      variantKey,
      setTimeout(function () {
        pendingTimers.delete(variantKey);
        form.updateItemQuantity(variantId, value, input.getAttribute('name'), input);
      }, DEBOUNCE_MS)
    );
  }

  // -----------------------------------------------------------------
  // Qty +/- buttons
  // -----------------------------------------------------------------
  function handleQtyButtonClick(event, button) {
    var wrapper = button.closest('quantity-input');
    var input = wrapper ? wrapper.querySelector('input.quantity__input') : null;
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    var rules = getRules(input);
    var current = parseInt(input.value, 10) || 0;
    var next;

    if (button.name === 'plus') {
      next = current === 0 ? rules.min : current + rules.increment;
      if (rules.max !== null) next = Math.min(next, rules.max);
    } else {
      next = current <= rules.min ? 0 : current - rules.increment;
      if (next > 0 && next < rules.min) next = rules.min;
    }

    input.setCustomValidity('');
    commitQuantity(input, next);
  }

  // -----------------------------------------------------------------
  // Typed quantity entry
  // -----------------------------------------------------------------
  function handleQtyInputChange(event, input) {
    if (!input.closest('quantity-input')) return;

    event.stopImmediatePropagation();

    var rules = getRules(input);
    var raw = input.value;
    var value = raw === '' ? 0 : parseInt(raw, 10);
    if (Number.isNaN(value)) value = 0;
    value = Math.max(0, value);

    var result = validateQuantity(value, rules);

    if (!result.valid) {
      input.setCustomValidity(result.message);
      input.reportValidity();
      input.value = parseInt(input.getAttribute('data-cart-quantity'), 10) || 0;
      return;
    }

    input.setCustomValidity('');
    commitQuantity(input, value);
  }

  // -----------------------------------------------------------------
  // Remove ("x") link
  // -----------------------------------------------------------------
  function handleRemoveClick(event, link) {
    event.preventDefault();
    event.stopImmediatePropagation();

    var form = getForm(link);
    if (!form || typeof form.updateItemQuantity !== 'function') return;

    var variantId = link.getAttribute('data-index');
    if (!variantId) return;

    form.updateItemQuantity(variantId, 0);
  }

  document.addEventListener(
    'click',
    function (event) {
      var qtyButton = event.target.closest('.quantity__button');
      if (qtyButton && withinBulkTable(qtyButton)) {
        handleQtyButtonClick(event, qtyButton);
        return;
      }

      var removeLink = event.target.closest('[is="quick-order-list-remove"]');
      if (removeLink && withinBulkTable(removeLink)) {
        handleRemoveClick(event, removeLink);
        return;
      }

      var sortButton = event.target.closest('.bulk-order-table__sort-btn');
      if (sortButton) {
        handleSortClick(sortButton);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target.closest('.quantity__input');
      if (input && withinBulkTable(input)) {
        handleQtyInputChange(event, input);
      }
    },
    true
  );

  // -----------------------------------------------------------------
  // Sortable headers
  // -----------------------------------------------------------------
  function getTables() {
    return Array.prototype.slice.call(document.querySelectorAll(TABLE_SELECTOR));
  }

  function sectionWrapperId(table) {
    var wrapper = table.closest('[id^="BulkOrderTable-"]');
    return wrapper ? wrapper.id : null;
  }

  function sortTable(table, key, direction) {
    var tbody = table.querySelector('tbody');
    if (!tbody) return;

    var rows = Array.prototype.slice.call(tbody.querySelectorAll('tr[data-sort-variant]'));
    var numeric = key === 'price' || key === 'availability';
    var attr = 'data-sort-' + key;

    rows.sort(function (a, b) {
      var aVal = a.getAttribute(attr) || '';
      var bVal = b.getAttribute(attr) || '';
      if (numeric) return (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
      return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
    });

    if (direction === 'desc') rows.reverse();
    rows.forEach(function (row) {
      tbody.appendChild(row);
    });

    Array.prototype.forEach.call(table.querySelectorAll('[data-sort-key]'), function (th) {
      if (th.dataset.sortKey === key) {
        th.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending');
      } else {
        th.removeAttribute('aria-sort');
      }
    });
  }

  function applyStoredSort(table) {
    var id = sectionWrapperId(table);
    if (!id) return;
    var state = sortState.get(id);
    if (!state) return;
    sortTable(table, state.key, state.direction);
  }

  function handleSortClick(button) {
    var th = button.closest('[data-sort-key]');
    var table = button.closest(TABLE_SELECTOR);
    if (!th || !table) return;

    var id = sectionWrapperId(table);
    var key = th.dataset.sortKey;
    var previous = id ? sortState.get(id) : null;
    var direction = previous && previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc';

    if (id) sortState.set(id, { key: key, direction: direction });
    sortTable(table, key, direction);
  }

  // The Quick order list form dispatches this after every successful
  // /cart/update.js call (see dispatchCartUpdateEvent in quick-order-list.js).
  document.addEventListener('cart:updated', function () {
    getTables().forEach(applyStoredSort);
  });

  function init() {
    getTables().forEach(function (table) {
      var id = sectionWrapperId(table);
      var defaultKey = table.dataset.defaultSort;
      if (!id || !defaultKey) return;
      if (!sortState.has(id)) {
        sortState.set(id, { key: defaultKey, direction: 'asc' });
      }
      var th = table.querySelector('[data-sort-key="' + defaultKey + '"]');
      if (th) th.setAttribute('aria-sort', 'ascending');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
